import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDomainConfig } from '../../../../lib/domainConfig';
import { expectUnreachable } from '../../../../lib/util';
import {
  BandSizeDetails,
  f1BandSizeOptions,
  f3BandSizeOptions,
  sizeDetailsForSizeOption,
  MeasurementUnit,
} from '../../../../lib/size';
import { BandSeries, SizeOption } from '../../../../types';
import SizingBlock from './SizingBlock';
import styles from './DynamicSizingGuide.module.scss';
import { BreedTranslationKeys, type SizingGuideItem } from './Dataset';
import DynamicSizingGuideResult from './DynamicSizingGuideResult';
import classNames from 'classnames';
import * as events from '../../../../lib/analytics/events';
import Loading from '../../../../components/Loading';

interface SizingGuideProps {
  bandSeries: BandSeries;
  onSelect: (newValue: SizeOption) => void;
  close?: () => void;
  measurementUnit?: MeasurementUnit;
}

interface SizingBlockProps {
  sizeOption: SizeOption;
  bandSizeDetails: BandSizeDetails;
  onSelect: (newValue: SizeOption) => void;
}

function SizingBlockForSizeOption({ sizeOption, bandSizeDetails, onSelect }: SizingBlockProps) {
  return (
    <SizingBlock
      key={sizeOption}
      dogList={bandSizeDetails.dogList}
      sizeName={bandSizeDetails.sizeName}
      neckSize={bandSizeDetails.neckSize}
      bandWidth={bandSizeDetails.bandWidth}
      onClick={() => onSelect(sizeOption)}
    />
  );
}

export async function getSizingGuideRecommendation(
  weight: number,
  breed: string,
  isPuppy: boolean,
): Promise<SizingGuideItem> {
  let sizingGuideResult: SizingGuideItem | undefined = undefined;
  // lazy import for code splitting
  const { AdultSizingGuide, GenericAdultsSizingGuide, GenericPuppySizingGuide, PuppySizingGuide } = await import(
    './Dataset'
  );
  const breedDataset = (isPuppy ? PuppySizingGuide : AdultSizingGuide).filter((row) => row.name === breed);
  const ceiledWeight = Math.ceil(weight / 10) * 10;
  sizingGuideResult = breedDataset.find((row) => row.weight === ceiledWeight);

  if (!sizingGuideResult) {
    const fallbackDataset: { [key: number]: any } = isPuppy ? GenericPuppySizingGuide : GenericAdultsSizingGuide;
    sizingGuideResult = fallbackDataset[ceiledWeight];
  }
  return sizingGuideResult || { total: 0, XS: 0, S: 0, M: 100, L: 0, XL: 0 };
}

function getBreedTranslationKey(breed: string): string {
  return BreedTranslationKeys[breed];
}

export default function DynamicSizingGuide({ bandSeries, onSelect, close, measurementUnit }: SizingGuideProps) {
  const { t } = useTranslation();
  const allSizeOptions = useMemo(() => {
    if (bandSeries === BandSeries.F1) {
      return f1BandSizeOptions.map((sizeOption) => ({
        sizeOption,
        bandSizeDetails: sizeDetailsForSizeOption({ bandSeries, sizeOption }),
      }));
    } else if (bandSeries === BandSeries.F3) {
      return f3BandSizeOptions.map((sizeOption) => ({
        sizeOption,
        bandSizeDetails: sizeDetailsForSizeOption({ bandSeries, sizeOption, measurementUnit }),
      }));
    } else {
      expectUnreachable(bandSeries);
      throw new Error(`Unsupported band series: ${bandSeries}`);
    }
  }, [bandSeries, measurementUnit]);

  const sizeOptionList = useMemo(() => {
    return allSizeOptions.map(
      ({ bandSizeDetails, sizeOption }) =>
        bandSizeDetails && (
          <SizingBlockForSizeOption
            key={sizeOption}
            sizeOption={sizeOption}
            bandSizeDetails={bandSizeDetails}
            onSelect={onSelect}
          />
        ),
    );
  }, [allSizeOptions, onSelect]);

  const [currentBreed, setCurrentBreed] = useState<string | undefined>(undefined);
  const [showBreedSelector, setShowBreedSelector] = useState<boolean>(false);
  const [currentBreedSearch, setCurrentBreedSearch] = useState<string | undefined>(undefined);
  const [currentWeight, setCurrentWeight] = useState<number>(30);
  const [userHasModifiedWeight, setUserHasModifiedWeight] = useState<boolean>(false);

  const [dataset, setDataset] = useState<typeof import('./Dataset') | null>(null);

  useEffect(() => {
    (async () => {
      const {
        SizingGuideDetermination,
        AdultSizingGuide,
        PuppySizingGuide,
        GenericAdultsSizingGuide,
        GenericPuppySizingGuide,
      } = await import('./Dataset');
      setDataset({
        SizingGuideDetermination,
        AdultSizingGuide,
        GenericAdultsSizingGuide,
        GenericPuppySizingGuide,
        PuppySizingGuide,
        BreedTranslationKeys,
      });
    })();
  }, []);

  if (!dataset) {
    return <Loading />;
  }

  const updateBreed = (breed: string) => {
    setCurrentBreed(breed);
    events.series3CollarSizingGuideBreedSelected(breed);
    if (!userHasModifiedWeight) {
      setCurrentWeight(Math.round(dataset.SizingGuideDetermination[breed].average_weight));
    }
  };
  const updateWeight = (newWeight: number) => {
    events.series3CollarSizingGuideWeightUpdate(currentWeight, newWeight);
    setCurrentWeight(newWeight);
    setUserHasModifiedWeight(true);
  };
  const showSizingErrorMessage = currentWeight > 200 || currentWeight < 5;

  let sizingGuideResult: SizingGuideItem | undefined = undefined;
  if (currentBreed && currentWeight && currentWeight >= 5 && currentWeight <= 200) {
    const weight = currentWeight;
    const averageWeight = dataset.SizingGuideDetermination[currentBreed]['average_weight'];
    const stdDev = dataset.SizingGuideDetermination[currentBreed]['weight_stddev'];

    const usePuppy = weight <= averageWeight - stdDev;
    const breedDataset = (usePuppy ? dataset.PuppySizingGuide : dataset.AdultSizingGuide).filter(
      (row) => row.name === currentBreed,
    );
    const ceiledWeight = Math.ceil(weight / 10) * 10;
    sizingGuideResult = breedDataset.find((row) => row.weight === ceiledWeight);

    if (!sizingGuideResult) {
      const fallbackDataset: { [key: number]: any } = usePuppy
        ? dataset.GenericPuppySizingGuide
        : dataset.GenericAdultsSizingGuide;
      sizingGuideResult = fallbackDataset[ceiledWeight];
    }

    if (sizingGuideResult) {
      sizingGuideResult.isPuppy = usePuppy;
    }
  }

  return (
    <>
      <div className={classNames(styles.dynamicSizingMain, showBreedSelector ? styles.dynamicSizingNoScroll : null)}>
        <div className={styles.dynamicSizingSection}>
          <div className={styles.dynamicSizingDetailsSection}>
            <div className={styles.header}>{t('collar_sizing')}</div>
            <div className={styles.subHeader}>{t('need_help_tell_us_more_about_your_dog')}</div>
            <button
              className={styles.breedButton}
              onClick={() => {
                setShowBreedSelector(true);
                events.series3CollarSizingGuideBreedSelectorShown();
                setCurrentBreedSearch(undefined);
              }}
            >
              {!currentBreed && <div className={styles.breedPlaceholder}>{t('breed')}</div>}
              {currentBreed && (
                <div className={styles.currentBreed}>{t(getBreedTranslationKey(currentBreed)) || currentBreed}</div>
              )}
              <div className={styles.buttonArrow} />
            </button>
            <button
              className={classNames(styles.sizingButton, showSizingErrorMessage ? styles.sizingButtonError : null)}
            >
              <button
                className={styles.minusButton}
                onClick={() => {
                  if (currentWeight - 1 < 5) return;
                  updateWeight(currentWeight - 1);
                }}
              >
                <div className={styles.minusButtonIcon} />
              </button>
              <div className={styles.weight}>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={currentWeight}
                  min="5"
                  max="200"
                  step="1"
                  onChange={(e) => updateWeight(parseInt(e.currentTarget.value))}
                ></input>
                &nbsp;<div className={styles.poundsText}>{t('pounds_abbreviation', { count: currentWeight })}</div>
              </div>
              <button
                className={styles.plusButton}
                onClick={() => {
                  if (currentWeight + 1 > 200) return;
                  updateWeight(currentWeight + 1);
                }}
              >
                <div className={styles.plusButtonIcon} />
              </button>
            </button>
            {showSizingErrorMessage && (
              <div className={styles.sizingErrorMessage}>{t('weight_must_be_between_5_200_lbs')}</div>
            )}
          </div>
          <div className={styles.dynamicSizingResultSection}>
            {sizingGuideResult && (
              <DynamicSizingGuideResult
                sizingGuideItem={sizingGuideResult}
                currentWeight={currentWeight}
                onSelect={(sizeOption: SizeOption) => {
                  events.series3CollarSizingGuideRecommendationSelected(currentBreed, currentWeight, sizeOption);
                  onSelect(sizeOption);
                  close && close();
                }}
              />
            )}
          </div>
        </div>
        <div className={styles.sizeOptionSection}>
          <div className={styles.header}>{t('general_sizing_suggestions')}</div>
          <div className={styles.sizeOptionList}>
            {sizeOptionList}
            <div className={styles.stillUnsure}>
              <h3>{t('still_unsure')}</h3>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://${getDomainConfig().supportDomain}/hc/en-us/articles/360018860034-Sizing-and-Fit`}
              >
                {t('check_out_this_article')}
              </a>
              <p>
                {t(
                  'recommendations_based_on_average_breed_sizes_all_dogs_may_vary_measure_your_dogs_existing_collar_length_for_the_most_accurate_size_estimate',
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.breedSelectorContainer}>
        <div className={showBreedSelector ? styles.breedSelector : styles.breedSelectorHidden}>
          <button className={styles.backButton} onClick={() => setShowBreedSelector(false)}>
            <div className={styles.backButtonIcon} />
          </button>
          <div className={styles.header}>
            <div className={styles.headerText}>{t('select_breed')}</div>
          </div>
          <input
            className={styles.searchBarInput}
            value={currentBreedSearch || ''}
            onChange={(e) => setCurrentBreedSearch(e.currentTarget.value)}
            placeholder={t('search')}
          />
          <ul>
            {Object.keys(dataset.SizingGuideDetermination)
              .sort((a, b) => {
                if (a === 'other') return 1;
                if (b === 'other') return -1;
                return a.localeCompare(b);
              })
              .filter((breed) =>
                currentBreedSearch ? breed.toLowerCase().includes(currentBreedSearch.toLowerCase()) : true,
              )
              .map((breed) => (
                <li className={styles.breedListItem} key={breed}>
                  <button
                    className={styles.breedListItemButton}
                    key={breed}
                    onClick={() => {
                      updateBreed(breed);
                      setShowBreedSelector(false);
                    }}
                  >
                    {t(getBreedTranslationKey(breed)) || breed}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </>
  );
}
